import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DocumentMeta from 'react-document-meta';
import PersonalityHeader from '../components/Header';
import SideCharacters from '../components/SideCharacters';
import { questionPlaceholderImage } from '../data/characters';
import { pageTitles, questionList } from '../data/questions';
import { computeDiagnosisResult, PersonalityAnswer } from '../data/scoring';
import { saveDiagnosisResult } from '../data/storage';
import Styles from './styles.module.css';

const QUESTIONS_PER_PAGE = 8;
const TOTAL_PAGES = 4;

const questionGroups = Array.from({ length: TOTAL_PAGES }, (_, page) =>
  questionList.slice(page * QUESTIONS_PER_PAGE, page * QUESTIONS_PER_PAGE + QUESTIONS_PER_PAGE),
);

const scaleColor = (i: number) => (i <= 3 ? '#E44E70' : i === 4 ? '#999999' : '#3694D9');
const scaleSize = (i: number) => 20 + (i === 1 || i === 7 ? 20 : i === 2 || i === 6 ? 12 : 4);

// ドット位置(1=左端「そう思う」〜7=右端「そう思わない」、表示・色・大きさ用)と
// 採点スコア(仕様書3章: 7=凄いそう思う 〜 1=凄いそう思わない)は向きが逆なので、
// 保存・比較のたびにこの変換を通す(8 - x で自己逆変換)。
const scoreForPosition = (position: number) => 8 - position;

const PersonalityQuestion = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<PersonalityAnswer[]>(
    () => Array(questionList.length).fill(null),
  );
  const [errorMessage, setErrorMessage] = useState('');
  const cardRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const answeredCount = selectedAnswers.filter((a) => a != null).length;
  const progressPercent = (answeredCount / questionList.length) * 100;

  const meta = {
    title: '性格診断 | memorii works',
    description: '32の質問に答えて、あなたの美容師像を診断します。',
    canonical: 'https://memorii-works.com/personality/question',
    meta: { charset: 'utf-8' },
  };

  const setAnswer = (globalIndex: number, value: number) => {
    setSelectedAnswers((prev) => {
      const next = [...prev];
      next[globalIndex] = value;
      return next;
    });

    const pageEnd = (currentPage + 1) * QUESTIONS_PER_PAGE;
    const nextIndex = globalIndex + 1;
    if (nextIndex < pageEnd) {
      requestAnimationFrame(() => {
        cardRefs.current[nextIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  };

  const goPrevious = () => {
    if (currentPage > 0) {
      setErrorMessage('');
      setCurrentPage((p) => p - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goNext = () => {
    const startIndex = currentPage * QUESTIONS_PER_PAGE;
    const endIndex = startIndex + QUESTIONS_PER_PAGE;
    const firstUnanswered = selectedAnswers.findIndex((a, i) => i >= startIndex && i < endIndex && a == null);

    if (firstUnanswered !== -1) {
      setErrorMessage(`${firstUnanswered + 1} 問に回答してください。`);
      return;
    }

    if (currentPage < TOTAL_PAGES - 1) {
      setErrorMessage('');
      setCurrentPage((p) => p + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const diagnosis = computeDiagnosisResult(selectedAnswers);
    saveDiagnosisResult(diagnosis);
    navigate(`/personality/result/${diagnosis.type}`, {
      replace: true,
      state: {
        introduce: false,
        results: diagnosis.results,
        rawScores: diagnosis.rawScores,
        axisStates: diagnosis.axisStates,
      },
    });
  };

  return (
    <DocumentMeta {...meta}>
      <PersonalityHeader title="性格診断" mode="close" />
      <SideCharacters imagePath={questionPlaceholderImage} />
      <div className={Styles.page}>
        {errorMessage && <div className={Styles.error}>{errorMessage}</div>}

        <div className={Styles.progressCard}>
          <div className={Styles.progressRow}>
            <span className={Styles.progressIcon}>?</span>
            <span className={Styles.progressCount}>{currentPage + 1} / {TOTAL_PAGES}</span>
          </div>
          <p className={Styles.progressTitle}>{pageTitles[currentPage]}</p>
          <div className={Styles.progressBar}>
            <div className={Styles.progressBarFill} style={{ width: `${progressPercent}%` }} />
          </div>
        </div>

        <div className={Styles.questions}>
          {questionGroups[currentPage].map((q, i) => {
            const globalIndex = currentPage * QUESTIONS_PER_PAGE + i;
            const questionNumber = globalIndex + 1;
            const choice = selectedAnswers[globalIndex];
            return (
              <div
                className={Styles.card}
                key={q.number}
                ref={(el) => {
                  cardRefs.current[globalIndex] = el;
                }}
              >
                <p className={Styles.number}>{String(questionNumber).padStart(2, '0')}</p>
                <p className={Styles.text}>{q.question}</p>
                <div className={Styles.scaleLabels}>
                  <span className={Styles.scaleLabelLeft}>そう思う</span>
                  <span className={Styles.scaleLabelRight}>そう思わない</span>
                </div>
                <div className={Styles.scaleRow}>
                  {Array.from({ length: 7 }, (_, idx) => idx + 1).map((i2) => {
                    const size = scaleSize(i2);
                    const color = scaleColor(i2);
                    const selected = choice === scoreForPosition(i2);
                    return (
                      <button
                        type="button"
                        key={i2}
                        className={Styles.scaleDot}
                        style={{ width: size, height: size, borderColor: color }}
                        aria-label={`${questionNumber}問目 選択肢${i2}`}
                        onClick={() => setAnswer(globalIndex, scoreForPosition(i2))}
                      >
                        {selected && (
                          <span className={Styles.scaleDotFill} style={{ backgroundColor: color, width: size * 0.65, height: size * 0.65 }} />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className={Styles.nav}>
          <button type="button" className={Styles.backButton} disabled={currentPage === 0} onClick={goPrevious}>
            戻る
          </button>
          <button type="button" className={Styles.nextButton} onClick={goNext}>
            {currentPage === TOTAL_PAGES - 1 ? '結果を見る' : `次へ (${currentPage + 1}/${TOTAL_PAGES})`}
          </button>
        </div>
      </div>
    </DocumentMeta>
  );
};

export default PersonalityQuestion;
